import router from '@/router';

/**
 * 带动画效果的导航插件
 * 
 * 使用方法:
 * this.$navigate('/path', 'fade'); // 淡入淡出效果
 * this.$navigate('/path', 'slide-right'); // 右侧滑入效果
 * this.$navigate('/path', 'slide-left'); // 左侧滑入效果
 * this.$navigate('/path', 'fade-transform'); // 默认混合效果
 * 
 * 也可以传递路由参数和动画选项:
 * this.$navigate({ path: '/path', query: { id: 1 } }, { 
 *   transition: 'fade',
 *   duration: 0.6,   // 动画持续时间(秒)
 *   delay: 0         // 动画延迟(秒)
 * });
 */

const navPlugin = {
  install(app) {
    // 设置全局样式变量
    let styleElement = document.createElement('style');
    document.head.appendChild(styleElement);
    let styleSheet = styleElement.sheet;
    
    // 默认配置
    const defaultOptions = {
      transition: 'fade-transform',
      duration: 0.5,
      delay: 0
    };
    
    // 获取当前应用实例上的transition name
    const getTransitionName = () => {
      return app.config.globalProperties.$transitionName || defaultOptions.transition;
    };
    
    // 设置transition name和动画时间
    const setTransitionOptions = (options = {}) => {
      const finalOptions = { ...defaultOptions, ...options };
      
      // 设置过渡名称
      app.config.globalProperties.$transitionName = finalOptions.transition;
      
      // 设置过渡时间（添加或更新CSS规则）
      const transitionSelector = `.${finalOptions.transition}-enter-active, .${finalOptions.transition}-leave-active`;
      const cssRule = `${transitionSelector} { transition-duration: ${finalOptions.duration}s !important; transition-delay: ${finalOptions.delay}s !important; }`;
      
      try {
        // 尝试找到已存在的规则并更新
        let ruleExists = false;
        for (let i = 0; i < styleSheet.cssRules.length; i++) {
          if (styleSheet.cssRules[i].selectorText === transitionSelector) {
            styleSheet.deleteRule(i);
            styleSheet.insertRule(cssRule, i);
            ruleExists = true;
            break;
          }
        }
        
        // 如果规则不存在，添加新规则
        if (!ruleExists) {
          styleSheet.insertRule(cssRule, styleSheet.cssRules.length);
        }
      } catch (e) {
        console.warn('无法动态设置过渡时间', e);
      }
      
      return finalOptions;
    };
    
    // 带动画的导航方法
    app.config.globalProperties.$navigate = (to, options = {}) => {
      // 处理不同的参数格式
      let transitionOptions = defaultOptions;
      
      if (typeof options === 'string') {
        // 如果options是字符串，则认为它是transition名称
        transitionOptions = { transition: options };
      } else if (typeof options === 'object') {
        // 如果options是对象，则合并配置
        transitionOptions = options;
      }
      
      // 设置过渡选项
      setTransitionOptions(transitionOptions);
      
      // 使用Vue Router进行导航
      return router.push(to);
    };
    
    // 带动画的返回方法
    app.config.globalProperties.$goBack = (options = {}) => {
      let transitionOptions = defaultOptions;
      
      if (typeof options === 'string') {
        transitionOptions = { transition: options };
      } else if (typeof options === 'object') {
        transitionOptions = options;
      }
      
      setTransitionOptions(transitionOptions);
      
      return router.back();
    };
    
    // 全局过渡名称
    app.provide('getTransitionName', getTransitionName);
    app.provide('setTransitionOptions', setTransitionOptions);
  }
};

export default navPlugin; 